<template>
    <div id="settings-page">
        <confirm id="confirmDeleteCustomField" ref="confirmDeleteCustomField" @confirm="deleteCustomField(customFieldToShowInModal.id)">
            <template slot="title">
                <span>
                    {{ t('orderFields-confirm_delete-title') }}
                    <b>"{{ customFieldToShowInModal.field_name }}"</b>?
                </span>
            </template>
            <template slot="body">
                <template v-if="segmentsRelations.length === 0 && automationsRelations.length === 0">
                    {{ t('orderFields-confirm_delete-no_relations') }}
                </template>
                <template v-else>
                    {{ t('orderFields-confirm_delete-linked_warning') }}
                    <template v-if="automationsRelations.length > 0">
                        {{ t('orderFields-confirm_delete-linked_automations') }}
                        <ul>
                            <li v-for="automation in automationsRelations" :key="automation.title">
                                {{ automation.title }}
                            </li>
                        </ul>
                    </template>
                    <template v-if="segmentsRelations.length > 0">
                        {{ t('orderFields-confirm_delete-linked_segments') }}
                        <ul>
                            <li v-for="segment in segmentsRelations" :key="segment.name">
                                {{ segment.name }}
                            </li>
                        </ul>
                    </template>
                    {{ t('orderFields-confirm_delete-warning_message') }}
                </template>
            </template>
        </confirm>

    <confirm id="confirmEditCustomFieldFull" ref="confirmEditCustomFieldFull" @confirm="save">
            <template slot="title">
                {{ t('orderFields-edit_modal-title') }}
            </template>
            <template slot="body">
                <form>
                    <div class="fields">
                        <div class="field">
                            <label for="edit-field-name">{{ t('orderFields-edit_modal-name_label') }}</label>
                            <input
                                id="edit-field-name"
                                name="field_name"
                                type="text"
                                v-model="form.field_name"
                                class="form-control"
                            />
                        </div>
                        <div class="field mt-2">
                            <label for="edit-field-type">{{ t('orderFields-edit_modal-type_label') }}</label>
                            <select
                                id="edit-field-type"
                                name="type"
                                v-model="form.type"
                                class="form-control"
                                disabled
                            >
                                <option value="text" v-lang="'text'"></option>
                                <option value="date" v-lang="'date'"></option>
                                <option value="user" v-lang="'user'"></option>
                                <option value="number" v-lang="'number'"></option>
                                <option value="coupon" v-lang="'coupon'"></option>
                                <option value="textarea" v-lang="'textarea'"></option>
                                <option value="checkbox" v-lang="'checkbox'"></option>
                            </select>
                        </div>
                        <div class="field mt-2" v-if="form.type === 'checkbox'">
                            <label>{{ t('orderFields-edit_modal-options_label') }}</label>
                            <div
                                v-for="(option, index) in form.options"
                                :key="index"
                                class="d-flex align-items-center mb-2"
                            >
                                <label :for="'edit-option-' + index" class="sr-only">{{ t('orderFields-edit_modal-option_label', [index + 1]) }}</label>
                                <input
                                    :id="'edit-option-' + index"
                                    :name="`options[${index}][label]`"
                                    v-model="option.label"
                                    type="text"
                                    class="form-control mr-2 my-2"
                                    :placeholder="t('orderFields-edit_modal-option_placeholder')"
                                />
                                <button
                                    type="button"
                                    class="btn btn-danger"
                                    @click="removeOption(index)"
                                    :disabled="form.options.length <= 2"
                                >
                                    {{ t('orderFields-edit_modal-remove_option') }}
                                </button>
                            </div>
                            <button type="button" class="btn btn-primary mt-2" @click="addOption">
                                {{ t('orderFields-edit_modal-add_option') }}
                            </button>
                        </div>
                    </div>
                </form>
            </template>
        </confirm>
        <confirm id="confirmEditOrderField" ref="confirmEditOrderField" @confirm="editOrderFieldName(customFieldToShowInModal.id)">
            <template slot="title">
                <span>
                    {{ t('orderFields-confirm_edit_name-title') }}
                    <b>"{{ customFieldToShowInModal.field_name }}"</b>?
                </span>
            </template>
            <template slot="body">
                {{ t('orderFields-confirm_edit_name-body') }}
            </template>
        </confirm>
        <div class="container-fluid">
            <div class="row">
                <div class="col-md-12 col-sm-12">
                    <div class="box clearfix">
                        <template v-if="state === 'loading'">
                            <loader :inline="true"></loader>
                        </template>
                        <template v-if="state === 'list'">
                            <h1>{{ t('orderFields-list-title') }}</h1>
                            <table class="table table-striped fields mt-4">
                                <tr v-for="field in fields" :key="field.id">
                                    <td>
                                        <div class="field-list-item d-flex align-items-center">
                                            <div class="field-icon-placeholder" :title="'icone: ui-' + (field.type || 'text')">
                                                <Icon :icon-class="iconFor(field.type)" size="13" style="color: var(--secondary);" />
                                            </div>
                                            <div class="field-list-body">
                                                <template v-if="editingFieldId === field.id">
                                                    <label :for="'inline-field-' + field.id" class="sr-only">{{ t('orderFields-sr-name_label') }}</label>
                                                    <input
                                                        :id="'inline-field-' + field.id"
                                                        :name="`fields[${field.id}][field_name]`"
                                                        type="text"
                                                        v-model="field.field_name"
                                                        class="form-control"
                                                        :ref="'editFieldInput-' + field.id"
                                                    />
                                                </template>
                                                <template v-else>
                                                    <div class="field-name">{{ field.field_name }}</div>
                                                    <div class="field-type"><span v-lang="field.type"></span></div>
                                                </template>
                                            </div>
                                        </div>
                                    </td>
                                    <td></td>
                                    <td class="field-actions">
                                        <button
                                            v-if="editingFieldId === field.id"
                                            @click="confirmEditFieldName(field)"
                                        >
                                            <Icon icon-class="ui-check" />
                                        </button>
                                        <button
                                            v-else
                                            @click="startEditingField(field)"
                                        >
                                            <Icon icon-class="ui-edit-05" :tooltip="t('orderFields-list-edit_tooltip')" />
                                        </button>
                                        <button
                                            class="trash"
                                            @click="openConfirmDeleteCustomField(field)"
                                        >
                                            <Icon icon-class="ui-trash-01" :tooltip="t('orderFields-list-delete_tooltip')" />
                                        </button>
                                    </td>
                                </tr>
                            </table>
                            <button
                                class="add-btn"
                                @click.prevent="addNew"
                                :disabled="is('saving')"
                            >
                                <Icon icon-class="ui-plus" />
                                {{ t('orderFields-list-add_button') }}
                            </button>
                        </template>
                        <template v-if="state === 'form'">
                            <h1>{{ t('orderFields-form-title') }}</h1>
                            <form>
                                <div class="fields">
                                    <div class="field">
                                        <label for="new-field-name">{{ t('orderFields-form-name_label') }}</label>
                                        <input
                                            id="new-field-name"
                                            name="field_name"
                                            type="text"
                                            v-model="form.field_name"
                                            class="form-control"
                                        />
                                    </div>
                                    <div class="field mt-2">
                                        <label for="new-field-type">{{ t('orderFields-form-type_label') }}</label>
                                        <select
                                            id="new-field-type"
                                            name="type"
                                            v-model="form.type"
                                            class="form-control"
                                        >
                                            <option
                                                value="text"
                                                v-lang="'text'"
                                            ></option>
                                            <option
                                                value="date"
                                                v-lang="'date'"
                                            ></option>
                                            <option
                                                value="user"
                                                v-lang="'user'"
                                            ></option>
                                            <option
                                                value="number"
                                                v-lang="'number'"
                                            ></option>
                                            <option
                                                value="coupon"
                                                v-lang="'coupon'"
                                            ></option>
                                            <option
                                                value="textarea"
                                                v-lang="'textarea'"
                                            ></option>

                                            <option value="checkbox" v-lang="'checkbox'"></option>
                                        </select>
                                    </div>
                                    <div class="field mt-2" v-if="form.type === 'checkbox'">
                                        <label>{{ t('orderFields-form-options_label') }}</label>

                                        <div
                                            v-for="(option, index) in form.options"
                                            :key="index"
                                            class="d-flex align-items-center mb-2"
                                        >
                                            <label :for="'new-option-' + index" class="sr-only">{{ t('orderFields-form-option_label', [index + 1]) }}</label>
                                            <input
                                            :id="'new-option-' + index"
                                            :name="`options[${index}][label]`"
                                            v-model="option.label"
                                            type="text"
                                            class="form-control mr-2 my-2"
                                            :placeholder="t('orderFields-form-option_placeholder')"
                                            />
                                            <button
                                            type="button"
                                            class="btn btn-danger"
                                            @click="removeOption(index)"
                                            :disabled="form.options.length <= 2"
                                            >
                                            {{ t('orderFields-form-remove_option') }}
                                            </button>
                                        </div>

                                        <button type="button" class="btn btn-primary mt-2" @click="addOption">
                                            {{ t('orderFields-form-add_option') }}
                                        </button>
                                    </div>

                                </div>
                            </form>
                            <div class="actions">
                                <a
                                    class="btn"
                                    href="#"
                                    @click.prevent="setState('list')"
                                    >{{ t('orderFields-form-back_button') }}</a
                                >
                                <button
                                    class="btn btn-success"
                                    @click.prevent="save"
                                    :disabled="is('saving')"
                                >
                                    {{ t('orderFields-form-save_button') }}
                                </button>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<style scoped>
.table.fields td {
    vertical-align: middle;
}
.actions {
    display: flex;
    justify-content: flex-end;
    gap: 20px;
    margin-top: 20px;
}
.field-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    button {
        background: transparent;
        border: none;
        color: #1F2A37;
    }
    button.trash {
        color: var(--danger);
    }
}

.add-btn {
    display: flex;
    padding: 12px;
    justify-content: center;
    align-items: center;
    gap: 4px;
    align-self: stretch;
    border-radius: 8px;
    border: 1px solid #E5E7EB;
    background: #FCFCFD;
    width: 100%;
}

.field-list-item {
    gap: 12px;
}
.field-icon-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 56px;
}
.field-icon-placeholder .placeholder-box {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    border: 1px dashed #E5E7EB;
    background: #FFFFFF;
}
.field-icon-placeholder .placeholder-label {
    font-size: 11px;
    color: #6B7280;
    margin-top: 6px;
    text-align: center;
}
.field-list-body .field-name {
    font-weight: 600;
}
.field-list-body .field-type {
    font-size: 12px;
    color: #9CA3AF;
}

.table.fields td {
    vertical-align: middle;
    border: none;
}
</style>
<script>
import keviApi from "../keviApi";
import { clearPdvOrderFieldsCache } from "../helpers/pdvOrderFields";
import Icon from "../components/icon/icon";

export default {
    props: {
        user: Object,
    },
    components: {
        Icon
    },
    data: function () {
        return {
            state: "loading",
            fields: [],
            form: {
                id: null,
                field_name: null,
                type: null,
                options: []
            },
            automationsRelations: [],
            segmentsRelations: [],
            customFieldToShowInModal: {
                id: null,
                field_name: null,
            },
            editingFieldId: null,
        };
    },
    computed: {
        place() {
            return this.$store.state.place;
        },
    },
    async mounted() {
        document.getElementsByTagName("title")[0].innerText =
            this.t("orderFields-page_title");
        await this.loadFields();
        this.setState("list");
    },
    methods: {
        t: function (term, replaces) {
            return this.$root.t(term, replaces);
        },
        addOption() {
            this.form.options.push({ label: "" });
        },
        iconFor(type) {
            switch (type) {
                case "date":
                    return "ui-calendar";
                case "user":
                    return "ui-user-square";
                case "number":
                    return "ui-number";
                case "textarea":
                    return "ui-type-square";
                case "checkbox":
                    return "ui-check-square-broken";
                case "coupon":
                    return "ui-ticket-01";
                default:
                    return "ui-type-square";
            }
        },
        removeOption(index) {
            this.form.options.splice(index, 1);
        },
        startEditingField(field) {
            if (field.type === "checkbox") {
                this.openEditCustomFieldModal(field);
                return;
            }
            this.editingFieldId = field.id;
            this.$nextTick(() => {
                const refName = `editFieldInput-${field.id}`;
                const el = this.$refs[refName];
                const inputEl = Array.isArray(el) ? el[0] : el;
                if (inputEl && typeof inputEl.focus === "function") {
                    inputEl.focus();
                }
            });
        },
        confirmEditFieldName(field) {
            this.customFieldToShowInModal = { ...field };
            this.$refs["confirmEditOrderField"].open();
        },
        editOrderFieldName(id) {
            const options = keviApi.getOptions(`order-fields/${id}`, "PUT", {
                name: this.customFieldToShowInModal.field_name,
            });
            this.$http(options).then(() => {
                clearPdvOrderFieldsCache();
                this.fields = this.fields.map((f) => {
                    if (f.id === id) f.field_name = this.customFieldToShowInModal.field_name;
                    return f;
                });
                this.$store.dispatch("notification/success", this.t("orderFields-notifications-field_edited"));
                this.editingFieldId = null;
            });
        },
        async loadFields() {
            let options = keviApi.getOptions("/order-fields?with-options=true", "GET");
            const response = await this.$http(options);
            this.fields = JSON.parse(JSON.stringify(response.body));
        },
        is: function (state) {
            return this.state === state;
        },
        setState: function (state) {
            this.state = state;
        },
        addNew() {
            this.setState("form");
            this.form = {
                id: null,
                field_name: null,
                type: null,
                options: []
            };
        },
        save() {
            const isEdit = !!this.form.id;

            let filledOptions = [];
            if (this.form.type === "checkbox") {
                filledOptions = this.form.options.filter(
                    option => option.label && option.label.trim() !== ""
                );

                if (filledOptions.length < 2) {
                    this.$store.dispatch(
                        "notification/warning",
                        this.t("orderFields-notifications-min_options_warning")
                    );
                    return;
                }
            }

            const body = {
                name: this.form.field_name,
                type: this.form.type,
                options: this.form.type === "checkbox"
                    ? filledOptions.map(opt => ({
                        text: opt.label.trim(),
                        new: false,
                        removed: false,
                        active: true
                    }))
                    : null,
                multiselect: filledOptions.length > 0
            };

            const options = keviApi.getOptions(
                isEdit ? `order-fields/${this.form.id}` : "order-fields",
                "PUT",
                body
            );

            window?.mixpanel?.track(isEdit ? "Settings - edit order field" : "Settings - save order field");

            this.$http(options).then(async () => {
                clearPdvOrderFieldsCache();
                this.$store.dispatch(
                    "notification/success",
                    isEdit ? this.t("orderFields-notifications-field_updated") : this.t("orderFields-notifications-field_added")
                );
                this.setState("loading");
                await this.loadFields();
                this.setState("list");
                this.editingFieldId = null;
            });
        },
        openConfirmDeleteCustomField(customField) {
            const options = keviApi.getOptions("order-fields/" + customField.id + "/relations", "GET");
            this.$http(options).then((response) => {
                this.automationsRelations = response.body.automations;
                this.segmentsRelations = response.body.segments;
            });
            this.customFieldToShowInModal = customField;
            this.$refs["confirmDeleteCustomField"].open();
        },
        deleteCustomField(id) {
            const options = keviApi.getOptions("order-fields/" + id, "DELETE");
            window?.mixpanel?.track("Settings - delete order field");
            this.$http(options).then(() => {
                clearPdvOrderFieldsCache();
                this.fields = this.fields.filter(
                    (customField) => customField.id !== id,
                );
                this.$store.dispatch(
                    "notification/success",
                    this.t("orderFields-notifications-field_deleted"),
                );
                this.setState("list");
            });
        },
        openEditCustomFieldModal(field) {
            this.form = {
                id: field.id,
                field_name: field.field_name,
                type: field.type,
                options: field.options && Array.isArray(field.options)
                    ? field.options.map(opt => ({
                        label: opt.text || opt.label || ""
                    }))
                    : []
            };

            if (this.form.type === "checkbox" && this.form.options.length < 2) {
                this.form.options = [{ label: "" }, { label: "" }];
            }

            this.$refs.confirmEditCustomFieldFull.open();
        }
    },
    watch: {
    "form.type"(newVal) {
        if (newVal === "checkbox" && this.form.options.length < 2) {
        this.form.options = [{ label: "" }, { label: "" }];
        } else if (newVal !== "checkbox") {
        this.form.options = [];
        }
    }
}

};
</script>
